import { getUpdatedLocaleForURLAfterRegionSwitch } from '@src/components/locale/utils'
import { DEFAULT_LOCALE_PREFIX, LOCALE_COOKIE } from '@src/config/localisation/locale-config.constants'
import type { TAbsoluteLocale, TCountry, TRegionDetail } from '@src/config/localisation/locale-config.types'
import { DEFAULT_RMS_REGION_DATA } from '@src/config/localisation/rms-data'
import regionDetailsHolder from '@src/quince-site-common/src/data-holders/regionDetails.holder'
import { STORE_PROGRAM_ID_KEY } from '@src/quince-site-common/src/services/cartCheckoutService/constants'
import getCookie from '@src/quince-site-common/src/utils/getCookie'
import { allUrlLocales, getStandardLocale, parseLocale } from '@src/utils/i18n.utils'
import { getLocaleFromUrlPath } from '@src/utils/urlLocale.utils'

const CHECKOUT_PATH = '/checkout'
const CHECKOUT_PATH_NEW = '/checkout/v2'
const LOADER_REDESIGN_QUERY_PARAM = 'loader_redesign'
const GUEST_REDIRECTION_QUERY_PARAM = 'guest_redirection'
interface RegionAliases {
  country?: Record<string, string>
}

const STATIC_DISPLAY_CODE_TO_CANONICAL_COUNTRY: Record<string, string> = {}
for (const region of DEFAULT_RMS_REGION_DATA as readonly { regionCode: string; regionDisplayCode?: string }[]) {
  const canonicalCountry = String(region.regionCode).toLowerCase()
  const displayCountry = String(region.regionDisplayCode ?? region.regionCode).toLowerCase()
  STATIC_DISPLAY_CODE_TO_CANONICAL_COUNTRY[canonicalCountry] = canonicalCountry
  STATIC_DISPLAY_CODE_TO_CANONICAL_COUNTRY[displayCountry] = canonicalCountry
}

// Use case: checkout redirects can include locale, country, or alias prefixes.
// Build a runtime prefix set from RMS so strip/compare logic stays correct for all regions.
const getDynamicUrlPrefixes = (regionDetails: TRegionDetail | null): Set<string> => {
  // Start with the canonical list used historically (countries + supported locales).
  const prefixes = new Set<string>(allUrlLocales.map(s => s.toLowerCase()))
  if (!regionDetails) return prefixes

  for (const [regionKey, region] of Object.entries(regionDetails.regions)) {
    if (!region) continue
    prefixes.add(regionKey.toLowerCase())
    prefixes.add(String(region.regionCode).toLowerCase())
    for (const locale of region.supportedLanguagesIsoCodes ?? []) {
      prefixes.add(String(locale).toLowerCase())
    }
  }

  const regionAliases = regionDetails.regionAliases as RegionAliases | undefined
  const countryAliases: Record<string, string> = regionAliases?.country ?? {}
  for (const [aliasCountry, canonicalCountry] of Object.entries(countryAliases)) {
    prefixes.add(aliasCountry.toLowerCase())
    prefixes.add(canonicalCountry.toLowerCase())
  }

  return prefixes
}

const resolveCanonicalCountry = (inputCountry: string, regionDetails: TRegionDetail | null): TCountry => {
  const normalized = inputCountry.toLowerCase()

  const regionAliases = regionDetails?.regionAliases as RegionAliases | undefined
  const countryAliases: Record<string, string> = regionAliases?.country ?? {}
  const aliased = countryAliases[normalized]?.toLowerCase()
  const staticCanonicalCountry = STATIC_DISPLAY_CODE_TO_CANONICAL_COUNTRY[normalized]

  return (aliased ?? staticCanonicalCountry ?? normalized) as TCountry
}

// Use case: determine redirect country from first URL segment even when path uses alias/display code.
const resolveCountryFromPathPrefix = (prefix: string | undefined, regionDetails: TRegionDetail | null): TCountry => {
  const normalizedPrefix = (prefix ?? '').toLowerCase()
  const prefixes = getDynamicUrlPrefixes(regionDetails)
  if (!normalizedPrefix || !prefixes.has(normalizedPrefix)) {
    // No locale/country prefix in the path; use the default locale's country for compatibility.
    return parseLocale(DEFAULT_LOCALE_PREFIX as TAbsoluteLocale).country
  }

  // Locale format: `en-us` -> country is the 2nd segment.
  if (normalizedPrefix.includes('-')) {
    const parts = normalizedPrefix.split('-')
    const countryPart = parts[1]
    if (!countryPart) return parseLocale(DEFAULT_LOCALE_PREFIX as TAbsoluteLocale).country
    return resolveCanonicalCountry(countryPart, regionDetails)
  }

  // Country/alias format: `us`, `ca`, `gb`, `uk`, etc (aliases are normalized via regionAliases.country).
  return resolveCanonicalCountry(normalizedPrefix, regionDetails)
}

export const getLocalePrefix = (): string => {
  // Use case: when cookie is missing, derive locale from URL with RMS context (alias-aware).
  try {
    const locale = getCookie(LOCALE_COOKIE) as TAbsoluteLocale
    if (locale) {
      const localePrefix = getStandardLocale(locale)
      return localePrefix
    }
    const regionDetails = regionDetailsHolder.getRegionDetails() ?? undefined
    return getLocaleFromUrlPath(DEFAULT_LOCALE_PREFIX, regionDetails)
  } catch (error) {
    console.error('Error getting locale prefix:', error)
    return DEFAULT_LOCALE_PREFIX
  }
}

/**
 * Creates a locale-prefixed checkout URL
 * @param baseUrl - The base checkout URL
 * @param checkoutId - The checkout ID
 * @param checkoutAuthCode - The checkout auth code (omitted from the URL when `hideAuthCode` is true)
 * @param hideAuthCode - When true, the URL contains only `checkoutId`.
 * @returns The complete checkout URL with locale prefix (only if different from default)
 */
export const createLocalePrefixedCheckoutUrl = (
  baseUrl: string,
  checkoutId: string,
  checkoutAuthCode: string,
  showNewDesign: boolean,
  isI18nPreviewEnabled: boolean,
  isB2BFlow: boolean,
  storeProgramId?: string,
  appendLoaderRedesign = false,
  loaderRedesignIsLoggedIn?: boolean,
  hideAuthCode = false,
): string => {
  const locale = (getCookie(LOCALE_COOKIE) as TAbsoluteLocale) || DEFAULT_LOCALE_PREFIX
  const regionDetails = regionDetailsHolder.getRegionDetails() as TRegionDetail

  const updatedLocale = getUpdatedLocaleForURLAfterRegionSwitch(locale, regionDetails)
  const url = new URL(baseUrl)

  const path = showNewDesign && !isB2BFlow ? CHECKOUT_PATH_NEW : CHECKOUT_PATH

  const updatedPath = isB2BFlow ? `${path}/b2b-checkout-form` : path

  // Only add locale prefix if it's different from the default locale
  if (updatedLocale === '' || !isI18nPreviewEnabled) {
    url.pathname = updatedPath
  } else {
    url.pathname = `/${updatedLocale}${updatedPath}`
  }

  url.searchParams.set('checkoutId', checkoutId)
  if (!hideAuthCode) {
    url.searchParams.set('checkoutAuthCode', checkoutAuthCode)
  }
  if (isB2BFlow && storeProgramId) {
    url.searchParams.set(STORE_PROGRAM_ID_KEY, storeProgramId)
  }
  if (appendLoaderRedesign) {
    url.searchParams.set(LOADER_REDESIGN_QUERY_PARAM, 'true')
    if (loaderRedesignIsLoggedIn !== undefined) {
      url.searchParams.set(GUEST_REDIRECTION_QUERY_PARAM, String(!loaderRedesignIsLoggedIn))
    }
  }

  return url.href
}

/**
 * Returns true if the checkout redirect URL is for a different region (country) than the current locale.
 * When true, the app should redirect to cart after login instead of checkout.
 */
export const isCheckoutUrlForDifferentRegion = (checkoutRedirectUrl: string): boolean => {
  // Use case: avoid false region mismatch for alias paths like /uk/checkout.
  try {
    const url = new URL(checkoutRedirectUrl)
    if (!url || typeof url.pathname !== 'string') return false
    const pathname = url.pathname
    const firstSegment = pathname
      .split('/')
      .find(s => s !== '')
      ?.toLowerCase()

    const regionDetails = regionDetailsHolder.getRegionDetails() ?? null
    const redirectCountry = resolveCountryFromPathPrefix(firstSegment, regionDetails)

    const currentLocale = getLocalePrefix().toLowerCase()
    const currentCountry = parseLocale(currentLocale as TAbsoluteLocale).country
    return redirectCountry !== currentCountry
  } catch {
    return false
  }
}

/**
 * Rewrites a checkout redirect URL so its path uses the current locale.
 * Used after login when the redirecturi may point to a different region (e.g. after region switch).
 * Prevents checkout/middleware from redirecting to cart on region mismatch.
 * @param checkoutRedirectUrl - Full checkout URL from redirecturi (same-origin or checkout subdomain)
 * @returns URL string with path updated to current locale, or null if parsing/rewrite fails
 */
export const rewriteCheckoutUrlToCurrentLocale = (checkoutRedirectUrl: string): string | null => {
  // Use case: rewrite redirect URLs using dynamic locale/country/alias prefixes from RMS.
  try {
    const url = new URL(checkoutRedirectUrl)
    if (!url || typeof url.pathname !== 'string') return null
    const pathname = url.pathname
    const parts = pathname.split('/').filter(Boolean)
    const firstSegment = parts.length > 0 ? parts[0] : undefined
    const regionDetails = regionDetailsHolder.getRegionDetails() ?? null
    const prefixes = getDynamicUrlPrefixes(regionDetails)
    const pathWithoutLocale =
      firstSegment && prefixes.has(firstSegment.toLowerCase()) ? '/' + parts.slice(1).join('/') : pathname

    const currentLocale = getLocalePrefix()
    const localeSegment = currentLocale ? currentLocale.toLowerCase() : ''
    const path = pathWithoutLocale ?? pathname
    const newPathname = localeSegment ? `/${localeSegment}${path}` : path
    url.pathname = newPathname
    return url.toString()
  } catch {
    return null
  }
}
